/**
 * What every overlay shares: the layer it sits on, the scrim behind a modal,
 * and how it appears.
 *
 * - **One layer.** Every overlay sits at `z-50`, above every page's own
 *   chrome (the highest is Admin's sticky top bar at `z-40`). Overlays opened
 *   from an overlay, such as a picker in a dialog or a dialog from the
 *   operator FAQ, stack by the order they open: Base UI appends each one to
 *   the end of `<body>`.
 * - **One scrim**, the theme's `overlay-scrim`, behind every modal: dialog,
 *   alert dialog and sheet, in every app. A page may blur what is behind it
 *   as well: Student's call screen does, over the coach's video.
 * - **Transitions, not keyframes.** Base UI marks an overlay
 *   `data-starting-style` as it mounts and `data-ending-style` as it leaves,
 *   and waits for the transition before unmounting. Transitions need no
 *   animation plugin, so the overlays move the same in Manager, which loads
 *   Tailwind without one, as in Student and Admin.
 */
/** The scrim behind a modal; a click on it dismisses a dialog or sheet, never an alert dialog. */
export const overlayBackdrop = 'fixed inset-0 z-50 bg-overlay-scrim transition-opacity duration-150 ' +
    'data-starting-style:opacity-0 data-ending-style:opacity-0 motion-reduce:transition-none';
/** A popup that opens from its trigger, scaling out of the side it opens from. */
export const floatingMotion = 'origin-(--transform-origin) transition-[opacity,scale] duration-100 ' +
    'data-starting-style:scale-95 data-starting-style:opacity-0 ' +
    'data-ending-style:scale-95 data-ending-style:opacity-0 motion-reduce:transition-none';
/** A centered modal, fading and scaling in place. */
export const modalMotion = 'transition-[opacity,scale] duration-150 ' +
    'data-starting-style:scale-95 data-starting-style:opacity-0 ' +
    'data-ending-style:scale-95 data-ending-style:opacity-0 motion-reduce:transition-none';
/** Centered in the viewport, with room to breathe on a phone. */
export const modalPosition = 'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 outline-none';
/**
 * The two modal shapes, carried by `size` as for buttons and fields:
 *
 * - **Dense** (`default`) is Admin's operator dialog: the dialog corner (10 px) and the
 *   high card shadow. The parts inside lay out its padding.
 * - **Customer** (`lg`) is Student's dialog card, the `Card` surface:
 *   the dialog corner (10 px), the card shadow and 28 px padding, 20 px at the sides on a
 *   phone. 420 px wide unless a call site widens it. It sets the learner
 *   font, as the customer button does: the dialog opens at the end of
 *   `<body>`, outside Student's `.sc` root. Its title and text take the
 *   page's own type classes, as the rest of the card does.
 */
export const modalSurface = {
    default: 'rounded-token-card-lg border border-border-default bg-surface-card text-text-primary shadow-card-hi',
    lg: 'max-h-[calc(100dvh-2rem)] max-w-[420px] overflow-y-auto overscroll-contain rounded-token-card-lg ' +
        'border border-border-default/70 bg-surface-card px-5 py-7 font-learner text-text-primary shadow-card sm:px-7',
};
/** The X in a dense dialog's or sheet's corner. */
export const cornerClose = 'absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-token-input ' +
    'text-text-secondary hover:bg-surface-card-hover hover:text-text-primary ' +
    'focus-visible:focus-ring-inset';
//# sourceMappingURL=overlay-shape.js.map